/**
 * VideoEmbed — renders a normalised video link (from
 * `resolve_video_url`) as an aspect-ratio-locked iframe.
 *
 * Accepts either the full server-normalised object or a raw
 * pasted URL (in which case it calls /api/video/resolve first).
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { PlayCircle, ExternalLink } from "lucide-react";

export default function VideoEmbed({ video, url, className = "" }) {
  const [meta, setMeta] = useState(video || null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    if (video || !url) { setMeta(video || null); return; }
    let cancel = false;
    (async () => {
      try {
        const r = await http.get(`/video/resolve`, { params: { url } });
        if (!cancel) setMeta(r.data);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Couldn't embed this video.");
      }
    })();
    return () => { cancel = true; };
  }, [video, url]);

  if (err) {
    return (
      <div className={`wofy-card !p-3 text-sm ${className}`} data-testid="video-embed-error">
        <div className="opacity-70 mb-1"><PlayCircle className="w-4 h-4 inline mr-1" />{err}</div>
        {url && <a href={url} target="_blank" rel="noreferrer" className="wofy-link text-xs inline-flex items-center gap-0.5">
          Watch on original site <ExternalLink className="w-3 h-3" />
        </a>}
      </div>
    );
  }
  if (!meta) return null;

  return (
    <div className={`relative w-full overflow-hidden rounded-lg bg-black ${className}`}
         style={{ aspectRatio: "16 / 9" }}
         data-testid="video-embed">
      <iframe
        src={meta.embed_url}
        title={meta.title || "Embedded video"}
        className="absolute inset-0 w-full h-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
